import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Alert,
  LinearProgress,
  Fade,
  Tooltip,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import api from '../../api';

function getPasswordRuleError(password) {
  if (!password) return 'Please enter a new password.';
  if (password.length < 8) return 'Password must be at least 8 characters.';
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(password))
    return 'Include uppercase, lowercase, a number, and a special character.';
  return '';
}

function getConfirmPasswordError(password, confirmPassword) {
  if (!confirmPassword) return 'Please confirm your password.';
  if (password !== confirmPassword) return 'Passwords do not match.';
  return '';
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getRoleLabel(role) {
  const r = (role || '').toLowerCase();
  if (r === 'admin') return 'Admin';
  if (r === 'college_admin' || r === 'collegeadmin' || r === 'college-admin') return 'Admin';
  if (r === 'club_member' || r === 'clubmember' || r === 'club' || r === 'club-member') return 'Club Member';
  if (r === 'student') return 'Student';
  return '';
}

function getLoginRouteForRole(role) {
  const r = (role || '').toLowerCase();
  if (r === 'student') return '/login/student';
  if (r === 'club_member' || r === 'clubmember' || r === 'club' || r === 'club-member') return '/login/club-member';
  if (r === 'admin' || r === 'college_admin' || r === 'collegeadmin' || r === 'college-admin') return '/login/admin';
  return '/login';
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const query = useQuery();

  const uid = (query.get('uid') || '').trim();
  const token = (query.get('token') || '').trim();

  const role = (query.get('role') || '').trim();
  const returnTo = (query.get('returnTo') || '').trim();
  const roleLabel = getRoleLabel(role);

  const loginTarget = useMemo(() => {
    if (returnTo && returnTo.startsWith('/')) return returnTo;
    return getLoginRouteForRole(role);
  }, [returnTo, role]);

  const forgotTarget = useMemo(() => {
    const qs = new URLSearchParams();
    if (role) qs.set('role', role);
    if (returnTo) qs.set('returnTo', returnTo);
    const q = qs.toString();
    return q ? `/forgot-password?${q}` : '/forgot-password';
  }, [role, returnTo]);

  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [touched, setTouched] = useState({ newPassword: false, confirmPassword: false });
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const linkInvalid = !uid || !token;

  const passwordRuleError = useMemo(() => getPasswordRuleError(newPassword), [newPassword]);
  const confirmRuleError = useMemo(
    () => (newPassword || confirmPassword ? getConfirmPasswordError(newPassword, confirmPassword) : ''),
    [newPassword, confirmPassword]
  );

  const showPasswordError = (attemptedSubmit || touched.newPassword) && !!passwordRuleError;
  const showConfirmError = (attemptedSubmit || touched.confirmPassword) && !!confirmRuleError;

  const canSubmit = !loading && !success && !linkInvalid && !passwordRuleError && !confirmRuleError;

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => navigate(loginTarget, { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [success, navigate, loginTarget]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAttemptedSubmit(true);
    setTouched({ newPassword: true, confirmPassword: true });

    if (linkInvalid) {
      setError('Invalid or expired reset link. Please request a new one.');
      return;
    }

    const pwErr = getPasswordRuleError(newPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }

    const confirmErr = getConfirmPasswordError(newPassword, confirmPassword);
    if (confirmErr) {
      setError(confirmErr);
      return;
    }

    setLoading(true);
    try {
      await api.post('users/password-reset/confirm/', {
        uid,
        token,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err) {
      const details = err?.details;
      const msg =
        (Array.isArray(details) && details.length ? details.join(' ') : null) ||
        err?.error ||
        err?.detail ||
        'Unable to reset password. Please request a new link and try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
      <Navbar />

      <Box sx={{ flex: 1, py: { xs: 4, md: 7 } }}>
        <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
          <Fade in timeout={600}>
            <Card
              elevation={0}
              sx={{
                borderRadius: { xs: 3, sm: 5 },
                border: '1px solid rgba(226, 232, 240, 0.9)',
                background: 'rgba(255, 255, 255, 0.9)',
                WebkitBackdropFilter: 'blur(12px)',
                backdropFilter: 'blur(12px)',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
              }}
            >
              {loading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #0D1C4F 0%, #1E3A8A 100%)' } }} />}

              <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', fontSize: { xs: '1.9rem', sm: '2.1rem' } }}>
                      Reset password{roleLabel ? ` (${roleLabel})` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, lineHeight: 1.45 }}>
                      Set a new password for your account. This link is valid for 30 minutes.
                    </Typography>
                  </Box>

                  {success && (
                    <Alert icon={<CheckIcon fontSize="inherit" />} severity="success" sx={{ borderRadius: 3 }}>
                      Password has been reset successfully. Redirecting to login…
                    </Alert>
                  )}

                  {!success && linkInvalid && (
                    <Alert severity="warning" sx={{ borderRadius: 3 }}>
                      This reset link is invalid or missing required information. Please request a new reset link.
                    </Alert>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 3 }}>
                      {error}
                    </Alert>
                  )}

                  {!success && (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={2}>
                      {!success && (
                        <>
                          <TextField
                            value={newPassword}
                            onChange={(e) => {
                              setNewPassword(e.target.value);
                              if (error) setError('');
                            }}
                            onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
                            label="New password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            fullWidth
                            disabled={loading}
                            error={showPasswordError}
                            helperText={showPasswordError ? passwordRuleError : ' '}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                                    <IconButton
                                      onClick={() => setShowPassword((s) => !s)}
                                      edge="end"
                                      disabled={loading}
                                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                                      sx={{ color: '#64748b' }}
                                    >
                                      {showPassword ? (
                                        <VisibilityOff fontSize="small" />
                                      ) : (
                                        <Visibility fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 4,
                                backgroundColor: 'rgba(248, 250, 252, 0.7)',
                              },
                            }}
                          />

                          <TextField
                            value={confirmPassword}
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                              if (error) setError('');
                            }}
                            onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
                            label="Confirm password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            fullWidth
                            disabled={loading}
                            error={showConfirmError}
                            helperText={showConfirmError ? confirmRuleError : ' '}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 4,
                                backgroundColor: 'rgba(248, 250, 252, 0.7)',
                              },
                            }}
                          />
                        </>
                      )}

                      {success ? (
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate(loginTarget)}
                          sx={{
                            py: 1.6,
                            borderRadius: 4,
                            textTransform: 'none',
                            fontWeight: 900,
                            color: '#ffffff',
                            background: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
                            boxShadow: '0 12px 30px rgba(13, 28, 79, 0.28)',
                          }}
                        >
                          Go to login
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={!canSubmit}
                          sx={{
                            py: 1.6,
                            borderRadius: 4,
                            textTransform: 'none',
                            fontWeight: 900,
                            color: '#ffffff',
                            background: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
                            boxShadow: '0 12px 30px rgba(13, 28, 79, 0.28)',
                            '&:hover': {
                              boxShadow: '0 16px 40px rgba(13, 28, 79, 0.35)',
                              transform: 'translateY(-1px)',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(255,255,255,0.9)',
                              background: 'linear-gradient(135deg, rgba(13, 28, 79, 0.45) 0%, rgba(30, 58, 138, 0.45) 100%)',
                            },
                          }}
                        >
                          Reset password
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="text"
                        startIcon={<BackIcon />}
                        onClick={() => navigate(forgotTarget)}
                        sx={{ textTransform: 'none', fontWeight: 800, color: '#475569', width: { xs: '100%', sm: 'auto' } }}
                      >
                        Back
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
